import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { AuthLayout } from './AuthLayout';
import { send, SetCookie, TOKEN_NAME } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { encrtypth } from '../service/CoreFunctions';
import { GoogleLogin } from '@react-oauth/google';
import { PasswordInput } from '../components/ui/PasswordInput';
import { getPostLoginRoute } from '../lib/authRoutes';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await send('io0010', {
      email,
      password: encrtypth(password)
    }, setLoading);
    sessionStorage.clear()
    localStorage.clear()
    checkedLogin(res)
  };

  const checkedLogin = (res) => {
    if (res?.status === 'error') {
      toast.error(res.message);
    } else {
      toast.success(t('welcomeBack'));
      SetCookie(TOKEN_NAME, res.token);
      setAuth(res);
      navigate(getPostLoginRoute(res.user));
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    const res = await send('io0018', {
      credential: credentialResponse.credential
    }, setLoading);
    checkedLogin(res);
  };

  return (
    <AuthLayout title={t('login')} subtitle={t('welcomeBack')}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">
            {t('email')}
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            {t('password')}
          </Label>
          <div className="relative">
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          variant="gradient"
          size="lg"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('loading')}
            </>
          ) : (
            t('login')
          )}
        </Button>
        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            {t('forgotPassword')}
          </Link>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-muted"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t('orContinueWith')}
            </span>
          </div>
        </div>

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => {
              toast.error(t('googleLoginFailed'));
            }}
            ux_mode="popup"
            width="100%"
          />
        </div>

        <p className="text-center text-sm text-muted-foreground">
          {t('noAccount')}{' '}
          <Link to="/register" className="text-primary hover:text-primary/80 font-semibold">
            {t('signUp')}
          </Link>
        </p>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-8">
        {t('termsAgree')}
      </p>
    </AuthLayout>
  );
};

export default Login;
